import * as React from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import {
  ClickAwayListener,
  Typography,
  Popover,
  FormControl,
  InputAdornment,
  Input,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
} from "@mui/material";
import { StyledFormControl } from "../../../../components/StyledComponents/Inputs/Inputs.js";
import { useHistory } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import styled from "@emotion/styled";
import { debounce, set } from "lodash";
import {
  BodyLarge,
  BodySmall,
  H6HeadlineSmall,
  H7TitleLarge,
} from "../../../../components/StyledComponents/Typography/Typography.tsx";
import {
  PrimaryButton,
  SecondaryButton,
  StyledIconButton,
} from "../../../../components/StyledComponents/Buttons/AuradineButtons.js";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { useEffect, useState } from "react";
import useMinerPageContext from "../../../context/useMinerPageContext.js";
import { useGetTagsList } from "../../../../api/queries/useGetTagsList.ts";
import TagList from "../../../../TagMangement/Components/TagList/TagList.js";
import { usePostAttachTagsToMiners } from "../../../queries/usePostAttachTagsToMiners.ts";
import { useQueryClient } from "@tanstack/react-query";
import useAppContextProvider from "../../../../AppContext/useAppContextProvider.js";
import { useTheme } from "../../../../ThemeContext/ThemeContext.js";

const StyledPopoverContent = styled(Box)(({ colors }) => ({
  display: "flex",
  flexDirection: "column",
  background: colors?.background?.popover,
  borderRadius: "1rem",
  boxShadow: colors?.boxShadow?.card || "0px 1px 2px 0px rgba(0, 0, 0, 0.3)",
}));

const StyledPopover = styled(Popover)(({ colors }) => ({
  "& .MuiPaper-root": {
    borderRadius: "1rem", // Change this to the border radius you want
    backgroundColor: colors?.background?.popover,
    boxShadow:
      colors?.boxShadow?.popover ||
      "0px 2px 6px 2px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.3)",
  },
}));

const StyledHeaderAndFooterBox = styled(Box)`
  padding: 1.5rem;
`;

const TagOperationsCard = (props) => {
  const {
    open,
    handleClose,
    anchorRef,
    refetch,
    setTableKey,
    userRole,
  } = props;
  const { colors } = useTheme();
  const [searchText, setSearchText] = useState("");
  const [dSearchText, setDSearchText] = useState("");
  const history = useHistory();
  const [authToken, setAuthToken] = React.useState("");
  const { getAccessTokenSilently, isAuthenticated } =
    useAuth0();

  useEffect(() => {
    const fetchToken = async () => {
      try {
        if (!isAuthenticated) {
          setAuthToken("");
          console.warn(
            "User is not authenticated. Fetching token..."
          );
          return;
        }
        const token = await getAccessTokenSilently({
          cacheMode: "cache-only",
          ignoreCache: true,
        });
        setAuthToken(token);
      } catch (error) {
        console.error(" Error fetching token:", error);
      }
    };
    fetchToken();
    const interval = setInterval(() => {
      fetchToken();
    }, 5 * 60 * 1000); // Refresh every 15 minutes

    return () => clearInterval(interval); // Cleanup on unmount
  }, [getAccessTokenSilently, isAuthenticated]);

  const debouncedUpdate = React.useMemo(
    () =>
      debounce((text) => {
        setDSearchText(text); // Update the search text state
      }, 300), // 300ms debounce delay
    []
  );

  const handleSearchChange = (event) => {
    const text = event.target.value;
    setSearchText(text);
    debouncedUpdate(text); // Trigger the debounced function
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: tagsDataRefetch,
    status,
    isFetched: isTagsDataFetched,
    isLoading: isTagsDataLoading,
  } = useGetTagsList({
    authToken,
    tagName: dSearchText,
  });

  let limit = 25;
  let [tagData, setTagData] = useState([]);
  // const [numberOfSelections, setNumberOfSelections] =
  //   useState(0);
  const {
    selectedSerials,
    setUntagMinersDialogOpen,
    setSelectedSerials,
  } = useMinerPageContext();

  const { setToastOpen, setToastMessage } =
    useAppContextProvider();

  const numberOfSelections = tagData?.filter(
    (tag) => tag.isTagSelected
  ).length;

  useEffect(() => {
    if (data && data?.pages) {
      const tagData =
        isTagsDataFetched && data?.pages.length > 0
          ? data?.pages
              .flatMap((page, pageIndex) =>
                page.msg.map((item, index) => {
                  // Generate a unique ID by combining the page index and the item index
                  return {
                    ...item,
                    id: pageIndex * limit + index,
                    isTagSelected: false,
                  };
                })
              )
              .sort((a, b) => {
                return a.tag.localeCompare(b.tag);
              })
          : [];
      setTagData((prevData) => {
        const finalData = tagData.map((item) => {
          return {
            ...item,
            isTagSelected:
              prevData.find(
                (prevItem) => prevItem.tag === item.tag
              )?.isTagSelected || false,
          };
        });
        return finalData;
      });
    }
  }, [data, isTagsDataFetched, limit]);

  const queryClient = useQueryClient();
  const { mutate: attachTagsToMiners } =
    usePostAttachTagsToMiners();

  const handleAttachTags = async () => {
    try {
      const selectedTags = tagData
        .filter((tag) => tag?.isTagSelected)
        .map((tag) => tag.tag);

      const payload = {
        minernames: selectedSerials,
        tags: selectedTags,
      };

      await attachTagsToMiners(payload, {
        onSuccess: async (data) => {
          if (data?.status === "ok") {
            const tagsData = tagData.map((tag) =>
              tag.isTagSelected
                ? { ...tag, isTagSelected: false }
                : { ...tag }
            );
            setTagData(tagsData);
            // setNumberOfSelections(0);
            await refetch();
            setSelectedSerials([]);
            setToastMessage("Tags attached successfully");
            setToastOpen(true);
            setTableKey((prevKey) => prevKey + 1);
          } else {
            console.error("Error attaching tags:", data);
            setToastMessage(
              "Error attaching tags: " + data?.msg
            );
            setToastOpen(true);
          }
        },
        onError: (error) => {
          console.error("Error attaching tags:", error);
          setToastMessage("Error attaching tags: " + error);
          setToastOpen(true);
        },
      });
    } catch (error) {
      console.error("Error attaching tags:", error);
    } finally {
      handleClose();
    }
  };
  return (
    <StyledPopover
      sx={{ zIndex: 9999, borderRadius: "1rem" }}
      open={open}
      anchorEl={anchorRef.current}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      colors={colors}
    >
      <ClickAwayListener onClickAway={handleClose}>
        <StyledPopoverContent colors={colors}>
          <StyledHeaderAndFooterBox
            display={"flex"}
            flexDirection={"column"}
            gap={4}
          >
            <Box
              display="flex"
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <H7TitleLarge>Add tag:</H7TitleLarge>
              <SecondaryButton colors={colors}
                onClick={handleAttachTags}
                disabled={
                  selectedSerials.length === 0 ||
                  numberOfSelections === 0
                }
                endIcon={
                  <span className="material-symbols-outlined">
                    new_label
                  </span>
                }
              >
                Attach
              </SecondaryButton>
            </Box>
            <BodySmall>
              Select which tags you’d like to mark.
            </BodySmall>
          </StyledHeaderAndFooterBox>
          <Box
            display="flex"
            flexDirection={"column"}
            padding="0rem 1.5rem"
            className={"search-box"}
          >
            {/* Search Input */}
            <StyledFormControl colors={colors}
              fullWidth
              sx={{ m: 1 }}
              variant="standard"
            >
              <Input
                id="standard-adornment-amount"
                placeholder="Search tags"
                sx={{
                  "& .MuiInputBase-input": {
                    color: colors?.text?.primary,
                    "::placeholder": {
                      color: colors?.text?.hint,
                    },
                  },
                  "& .MuiInputBase-inputMultiline": {
                    color: colors?.text?.primary,
                    "::placeholder": {
                      color: colors?.text?.hint,
                    },
                  },
                }}
                value={searchText}
                onChange={(event) =>
                  handleSearchChange(event)
                }
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: colors?.text?.muted }} />
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment position="end">
                    <StyledIconButton
                      colors={colors}
                      onClick={() => {
                        setSearchText("");
                        setDSearchText("");
                      }}
                    >
                      <CloseIcon sx={{ color: colors?.text?.muted }} />
                    </StyledIconButton>
                  </InputAdornment>
                }
              />
            </StyledFormControl>

            {/* Groups List */}
            <Box
              display={"flex"}
              flexDirection={"column"}
              width={"22.5rem"}
              minWidth={"22.5rem"}
              maxHeight={"15rem"}
              minHeight={"15rem"}
              maxWidth={"45rem"}
              overflow={"auto"}
            >
              <TagList
                tagList={tagData}
                isTagsDataLoading={isTagsDataLoading}
                isTagsDataFetched={isTagsDataFetched}
                hasNextPage={hasNextPage}
                fetchNextPage={fetchNextPage}
                isFetchingNextPage={isFetchingNextPage}
                setTagData={setTagData}
                attachTagsMode={true}
                detachTagsMode={false}
              />
            </Box>
          </Box>
          <Box>
            <List
              sx={{
                padding: "0.25rem 1.5rem",
                borderTop: "2px solid #CAC4D0",
              }}
            >
              <ListItemButton
                sx={{
                  display: "flex",
                  height: "3rem",
                  minHeight: "3rem",
                  "&:hover": {
                    backgroundColor:
                      colors.hover.muted,
                    borderRadius: "6.25rem",
                  },
                }}
                onClick={() => {
                  setUntagMinersDialogOpen(true);
                  handleClose();
                }}
              >
                <ListItemText>
                  <BodyLarge>
                    {" "}
                    Detach multiple tags{" "}
                  </BodyLarge>
                </ListItemText>
                <span className="material-symbols-outlined" style={{ color: colors?.text?.muted }}>
                  label_off
                </span>
              </ListItemButton>
              <ListItemButton
                sx={{
                  display: "flex",
                  height: "3rem",
                  minHeight: "3rem",
                  "&:hover": {
                    backgroundColor:
                      colors.hover.muted,
                    borderRadius: "6.25rem",
                  },
                }}
              >
                <ListItemText
                  onClick={() =>
                    history.push("/tagmanagement")
                  }
                >
                  <BodyLarge> Manage tags</BodyLarge>
                </ListItemText>
                <ArrowRightIcon sx={{ color: colors?.text?.muted }} />
              </ListItemButton>
            </List>
          </Box>
        </StyledPopoverContent>
      </ClickAwayListener>
    </StyledPopover>
  );
};

export default TagOperationsCard;

export const TagOperationsTab = (props) => {
  const {
    refetch: minerPageRefetch,
    setTableKey,
    userRole,
  } = props;
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef(null);
  const { user } = useAuth0();
  const { selectedSerials } = useMinerPageContext();
  const { colors } = useTheme();
  // Assuming this is coming from context or props
  const navigate = useHistory();
  const handleClick = (event) => {
    event.stopPropagation();
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };
  return (
    <React.Fragment>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          textAlign: "center",
          // paddingRight: "2rem",
        }}
      >
        <SecondaryButton colors={colors}
          ref={anchorRef}
          onClick={handleClick}
          disabled={selectedSerials.length === 0}
          endIcon={
            <span className="material-symbols-outlined">
              new_label
            </span>
          }
        >
          Tag
        </SecondaryButton>
      </Box>

      {open && (
        <TagOperationsCard
          open={open}
          handleClose={handleClose}
          anchorRef={anchorRef}
          refetch={minerPageRefetch}
          setTableKey={setTableKey}
          userRole={userRole}
        />
      )}
    </React.Fragment>
  );
};
